import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRegistration } from '@/context/RegistrationContext';
import { WREG_ROUTE, wregCarriedSearch } from '@/components/webreg/wregRoutes';
import type { WregStep } from '@/components/webreg/wregSteps';
import { wregStepIsAccessible } from '@/components/webreg/wregGates';
import { wregResumeStep } from '@/components/webreg/wregResume';
import { isWregDevPanelEnabled } from '@/components/webreg/WregDevPanel';

export {
  WREG_GATE,
  wregStepIsAccessible,
  type WregGate,
  type WregGateCtx,
} from '@/components/webreg/wregGates';

export type WregFlowGate = {
  ready: boolean;
  allowed: boolean;
};

export const useWregFlow = (step: WregStep): WregFlowGate => {
  const { registrationData, isLoaded } = useRegistration();
  const navigate = useNavigate();
  const location = useLocation();

  const allowed =
    isLoaded &&
    wregStepIsAccessible(step, {
      data: registrationData,
      devEnabled: isWregDevPanelEnabled(),
    });

  useEffect(() => {
    if (!isLoaded || allowed) return;
    // RND-37692: never bounce to `/`. Landing owns Start Over, and until this change every
    // bounce arrived there and wiped a half-finished registration. `wregResumeStep` only
    // returns a step whose own gate already passes, so the bounce terminates; the carried
    // search keeps `?invite=true` and the dev/scenario params across it.
    navigate(
      {
        pathname: WREG_ROUTE[wregResumeStep(registrationData)],
        search: wregCarriedSearch(location.search),
      },
      { replace: true },
    );
  }, [isLoaded, allowed, navigate, registrationData, location.search]);

  return { ready: isLoaded, allowed };
};
